import {Application} from '@app/views/admin/application-settings/models/application.model';
import {DirectoryOrganization, OrgType, TASK_TO_PROFILE_MAPPING} from '@app/core/model/entity-profile';
import {IntegrationState} from '@app/views/admin/application-settings/models/integration-state.model';
import {ApplicationGalleryModel} from '@app/views/admin/application-settings/models/application-gallery.model';
import {GeoCountry, GeoMetadata, GeoState, StateStat} from '@app/core/model/geo';
import {ISchoolPassportSsoAppModel} from '@app/views/admin/provider/app-gallery/app-gallery.service';
import {ProjectsDashboardService} from '@app/views/dashboard/projects.service';
import {BehaviorSubject, finalize, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

export class WidgetData {
    public additionalCounter = 0;

    constructor(public title: string,
                public label: string,
                public problemType: ProblemType,
                public hasNew: boolean = true,
                public count: number = 0,
                public newCount: number = 0,
                public error: string = null,
                public errorDetails: string = null,
                public isFilterSupported = true) {
        this.title = title;
        this.label = label;
        this.problemType = problemType;
        this.hasNew = hasNew;
        this.count = count;
        this.newCount = newCount;
        this.error = error;
        this.errorDetails = errorDetails;
        this.isFilterSupported = isFilterSupported;
    }

    static checkIsReportInContext(report: IssueReportItem, context: IssueReportsContext): boolean {
        return !context || context.guidsSet.has(report[context.property]);
    }

    static getNewIssuesDates(problemType: ProblemType = null): [Date, Date] {
        const currentDay = new Date().getDay();
        let startDateNumberOfLastDays = 1;
        let endDateNumberOfLastDays = 0;
        if (problemType === ProblemType.CONNECTION) {
            endDateNumberOfLastDays = 2;
            if (currentDay === 1) {
                // For Monday: Thursday, Friday, Saturday
                startDateNumberOfLastDays = 4;
            } else {
                // f.e. for Wednesday: Monday
                startDateNumberOfLastDays = 2;
            }
        } else {
            if (currentDay === 1) {
                // For Monday: Friday, Saturday, Sunday, Monday
                startDateNumberOfLastDays = 3;
            } else if (currentDay === 0) {
                // For Sunday: Friday, Saturday, Sunday
                startDateNumberOfLastDays = 2;
            }
        }
        const timeMs = new Date().getTime();
        const newStartDate = new Date(timeMs - 60 * 60 * 24 * 1000 * startDateNumberOfLastDays);
        const newEndDate = new Date(timeMs - 60 * 60 * 24 * 1000 * endDateNumberOfLastDays);
        return [
            new Date(newStartDate.getFullYear(), newStartDate.getMonth(), newStartDate.getDate()),
            new Date(newEndDate.getFullYear(), newEndDate.getMonth(), newEndDate.getDate(), 23, 59, 59, 999)
        ];
    }

    public setCounters(reports: IssueReportItem[] = [], reportsContext: IssueReportsContext, constTotal: number = null) {
        if (reports && reports.length > 0) {
            if (reportsContext) {
                const reportsInContext = reports.filter((report: IssueReportItem) =>
                    WidgetData.checkIsReportInContext(report, reportsContext)
                );

                this.count = reportsInContext.length;
                this.newCount = this.hasNew ? this.getNewIssueReportCounter(reportsInContext) : null;
            } else {
                this.count = (constTotal ? constTotal : reports.length);
                this.newCount = this.hasNew ? this.getNewIssueReportCounter(reports) : null;
            }
        }
    }

    public setConstCounters(constTotal: number, constNew: number, reportsContext: IssueReportsContext) {
        this.count = (reportsContext ? null : constTotal);
        this.newCount = (reportsContext ? null : constNew);
    }

    private getNewIssueReportCounter(list: IssueReportItem[]): number {
        if (!list || (list.length === 0)) {
            return 0;
        }
        const [newIssuesStartDate, newIssuesEndDate] = WidgetData.getNewIssuesDates(list[0].problemType);
        return list.filter((item: IssueReportItem) =>
            item.creationTs
            && (newIssuesStartDate <= item.creationTs)
            && (item.creationTs <= newIssuesEndDate)
        ).length;
    }

    getWeekDayNames(start: Date, end: Date): string[] {
        const result: string[] = [];
        const current = start;
        while (current < end) {
            result.push(current.toLocaleString('default', {weekday: 'long'}));
            current.setDate(current.getDate() + 1);
        }
        return result;
    }

    getNewIssuesWeekDayNames(problemType: ProblemType) {
        const [newIssuesStartDate, newIssuesEndDate] = WidgetData.getNewIssuesDates(problemType);
        return this.getWeekDayNames(newIssuesStartDate, newIssuesEndDate);
    }
}

export class StatWidgetData {
    constructor(public title: string,
                public label: string,
                public eduToneEntity: string,
                public type: string,
                public count: number = 0
    ) {
        this.title = title;
        this.label = label;
        this.eduToneEntity = eduToneEntity;
        this.type = type;
        this.count = count;
    }
}

export class SSOPortalWidgetData {
    private dataSubject = new BehaviorSubject<SSOPortalWidgetDataItem[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false);
    private loadingErrorSubject = new BehaviorSubject<boolean>(false);

    public data$ = this.dataSubject.asObservable();
    public loading$ = this.loadingSubject.asObservable();
    public loadingError$ = this.loadingErrorSubject.asObservable();
    public daysFilter = 1;

    constructor(public dashboardService: ProjectsDashboardService,
                public hasAccessRoles: string[],
                public incidentTypeFilter: string,
                public byFilter: string,
                public title: string,
                public col1Title: string,
                public col2Title: string,
                public filterAttr: string,
                public showDateFilter = true,
                public chartView = false,
                public error: string = null,
                public errorDetails: string = null,
                public limit: number = 5
    ) {
    }

    loadWidgetData(): Observable<SSOPortalWidgetDataItem[]> {
        this.loadingSubject.next(true);

        const startDate = new Date();
        startDate.setDate(startDate.getDate() - this.daysFilter);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date();

        return this.dashboardService.getTopSSOPortalIssues(
            this.incidentTypeFilter.toLowerCase(),
            this.byFilter,
            null,
            startDate,
            endDate,
            this.limit
        ).pipe(
            catchError(() => {
                this.loadingErrorSubject.next(true);
                return of([]);
            }),
            finalize(() => this.loadingSubject.next(false)),
            map((data: any[]) => {
                this.dataSubject.next(data);

                return data;
            })
        );
    }

    disconnect() {
        this.dataSubject.complete();
        this.loadingSubject.complete();
        this.loadingErrorSubject.complete();
    }
}

export class SSOPortalWidgetDataItem {
    constructor(public name: string,
                public guid: string,
                public count: number) {
    }
}

export class IssueReportItem {
    _defaultApplication: Application;
    _organization: DirectoryOrganization;
    isActionDisabled?: boolean;

    defaultApplicationLowerCaseName: string;
    organizationLowerCaseName: string;

    constructor(public organizationGuid: string,
                public applicationGuid: string,
                public defaultApplicationGuid: string,
                public problemType: ProblemType = null,
                public creationTs: Date = null) {
        this.organizationGuid = organizationGuid;
        this.applicationGuid = applicationGuid;
        this.defaultApplicationGuid = defaultApplicationGuid;
        this.problemType = <ProblemType>problemType;
        this.creationTs = creationTs ? new Date(creationTs) : null;
    }

    get defaultApplication(): Application {
        return this._defaultApplication;
    }

    set defaultApplication(defaultApplication: Application) {
        this._defaultApplication = defaultApplication;
        this.defaultApplicationLowerCaseName = defaultApplication?.name?.toLowerCase();
    }

    get organization(): DirectoryOrganization {
        return this._organization;
    }

    set organization(organization: DirectoryOrganization) {
        this._organization = organization;
        this.organizationLowerCaseName = organization?.orgName?.toLowerCase();
    }
}

export class SynchronizationIssueReportItem extends IssueReportItem {
}

export class SchoolMappingIssueReportItem extends IssueReportItem {
    constructor(organizationGuid: string,
                applicationGuid: string,
                defaultApplicationGuid: string,
                problemType: ProblemType,
                creationTs: Date,
                public vanishedFromMapping: number,
                public appearedInDs: number,
                public notMapped: number,
    ) {
        super(organizationGuid, applicationGuid, defaultApplicationGuid, problemType, creationTs);
        this.vanishedFromMapping = vanishedFromMapping;
        this.appearedInDs = appearedInDs;
        this.notMapped = notMapped;
    }
}

export class ApplicationWithoutScheduleReportItem extends IssueReportItem {
}

export class ApplicationReportItem extends IssueReportItem {
    constructor(organizationGuid: string,
                applicationGuid: string,
                defaultApplicationGuid: string,
                problemType: ProblemType,
                creationTs: Date,
                public state: IntegrationState
    ) {
        super(organizationGuid, applicationGuid, defaultApplicationGuid, problemType, creationTs);
        this.state = state;
    }
}

export class DataAccessIssueReportItem extends IssueReportItem {
    constructor(organizationGuid: string,
                applicationGuid: string,
                defaultApplicationGuid: string,
                problemType: ProblemType,
                creationTs: Date,
                public guid: string,
                public integrationId: string,
    ) {
        super(organizationGuid, applicationGuid, defaultApplicationGuid, problemType, creationTs);
        this.guid = guid;
        this.integrationId = integrationId;
    }
}

export class ExpireSoonIssueReportItem extends IssueReportItem {
    constructor(organizationGuid: string,
                applicationGuid: string,
                defaultApplicationGuid: string,
                problemType: ProblemType,
                public expirationTs: Date
    ) {
        super(organizationGuid, applicationGuid, defaultApplicationGuid, problemType, null);
        this.expirationTs = expirationTs ? new Date(expirationTs) : null;
    }
}

export class InternalSyncIssueReportItem extends IssueReportItem {
    constructor(organizationGuid: string,
                applicationGuid: string,
                defaultApplicationGuid: string,
                problemType: ProblemType,
                creationTs: Date,
                public transactionId: string,
                public tenant: string = ''
    ) {
        super(organizationGuid, applicationGuid, defaultApplicationGuid, problemType, creationTs);
        this.transactionId = transactionId;
        this.tenant = tenant;
    }
}

export class SSOPortalIssueReportItem extends IssueReportItem {
    errorTypeTitle: string;
    defaultSSOApplication: ISchoolPassportSsoAppModel;

    constructor(organizationGuid: string,
                applicationGuid: string,
                defaultApplicationGuid: string, // ToDo: create the list of predefined idp default applications
                problemType: ProblemType,
                creationTs: Date,
                public idp: string = '',
                public schoolGuid: string = '',
                public schoolName: string = '',
                public host: string = '',
                public userGuid: string = '',
                public username: string = '',
                public userRole: string = '',
                public errorType: string = '',
                public errorMessage: string = '',
                public requestUrl: string = '',
                public userAgent: string = '',
                public totalPagesCount: number = null,
                public totalRecordsCount: number = null
    ) {
        super(organizationGuid, applicationGuid, defaultApplicationGuid, problemType, creationTs);
        this.idp = SSOPortalIssueReportItem.getTrimByLastChar(idp, '_');
        this.schoolGuid = schoolGuid;
        this.schoolName = schoolName;
        this.host = host;
        this.userGuid = userGuid;
        this.username = username;
        this.userRole = userRole;
        this.errorType = errorType;
        this.errorTypeTitle = (ErrorTypeTitleMap.has(this.errorType)
            ? ErrorTypeTitleMap.get(this.errorType)
            : SSOPortalIssueReportItem.getErrorTypeTitle(this.errorType));
        this.errorMessage = errorMessage;
        this.requestUrl = requestUrl;
        this.userAgent = userAgent;
        this.totalPagesCount = totalPagesCount;
        this.totalRecordsCount = totalRecordsCount;
    }

    static getTrimByLastChar(idp: string, char: string = '_'): string {
        const lastChar = (idp || '').lastIndexOf(char);
        if (lastChar > -1) {
            return idp.substring(0, lastChar);
        }
        return idp;
    }

    static getProblemType(type: string): ProblemType {
        return ETXProblemTypesMap.has(type)
            ? ETXProblemTypesMap.get(type)
            : null;
    }

    static getETXIncidentType(problemType: ProblemType): string {
        return Array.from(ETXProblemTypesMap.keys()).find((key: string) =>
            ETXProblemTypesMap.get(key) === problemType
        );
    }

    static getErrorTypeTitle(errorType: string): string {
        return errorType;
    }
}

export interface SSOPortalIssuesFilters {
    problemType: ProblemType;
    host: string;
    districtGuid?: string;
    schoolGuid?: string;
    orgGuid?: string;
    appGuid?: string;
    idp?: string;
    username: string;
    userGuid: string;
    userRole: string;
    errorType: string;
    errorMessage: string;
    startDate?: Date;
    endDate?: Date;
}

export interface ETXIncident {
    type: string;
    districtGuid: string;
    schoolGuid: string;
    schoolName: string;
    host: string;
    userGuid: string;
    username: string;
    userRole: string;
    idp: string;
    appId: string;
    incidentType: string;
    stacktrace: string;
    requestUrl: string;
    userAgent: string;
    date: number;
    totalPagesCount: number;
    totalRecordsCount: number;
}

export interface ETXFailedSessionsSummary {
    districtGuid: number;
    total: number;
}

export interface ETXIncidentsSummary {
    loginTotal: number;
    loginNew: number;
    ssoTotal: number;
    ssoNew: number;
}

export interface DataAccessProgress {
    integrationId: string;
    applicationId: string;
    orgId: string;
    progress: number;
    started: Date;
    finished: Date;
    stepName: DataAccessStepName;
    expectedRemainingTime?: string;
    application?: Application;
    organization?: DirectoryOrganization;
}

export interface DataAccessIntegrationDetails {
    integrationId: string;
    notGrantedEntities: Array<{ guid: string; type: string }>;
    hasMissedEvents: boolean;
    lastStepExecutionDetails: DataAccessExecutionDetail[];
}

export interface DataAccessExecutionDetail {
    applicationGuid: string;
    finished: Date;
    id: string;
    integrationDetails: Object;
    integrationId: string;
    orgId: string;
    sessionId: number;
    started: Date;
    status: DataAccessExecutionStatus;
    stepName: DataAccessStepName;
}

export interface IssuesFilter {
    search: string;
    problemType: ProblemType;
    startDate: Date;
    endDate: Date;
}

export enum ProblemType {
    APPLICATION_WITHOUT_SCHEDULE = 'APPLICATION_WITHOUT_SCHEDULE',
    EXPIRED_APPLICATION = 'EXPIRED_APPLICATION',
    EXPIRE_SOON_INTEGRATION = 'EXPIRE_SOON_INTEGRATION',
    PENDING_APPLICATION = 'PENDING_APPLICATION',
    SUSPENDED_APPLICATION = 'SUSPENDED_APPLICATION',
    SUSPENDED_DUE_SCHOOL_YEAR_ROLL_OVER_PERIOD = 'SUSPENDED_DUE_SCHOOL_YEAR_ROLL_OVER_PERIOD',
    REJECTED_PP_APPLICATION = 'REJECTED_PP_APPLICATION',
    CONNECTION = 'CONNECTION',
    WARN = 'WARN',
    SCHOOL_MAPPING_WITH_VANISHED_SCHOOL = 'SCHOOL_MAPPING_WITH_VANISHED_SCHOOL',
    SCHOOL_MAPPING = 'SCHOOL_MAPPING',
    FAILED_SYNC = 'FAILED_SYNC',
    SYNCHRONIZATION = 'SYNCHRONIZATION',
    DATA_ACCESS = 'DATA_ACCESS',
    CHANGES_PENDING_APPROVAL_BY_SYSADMIN = 'CHANGES_PENDING_APPROVAL_BY_SYSADMIN',
    CHANGES_PENDING_APPROVAL = 'CHANGES_PENDING_APPROVAL',
    DATA_VERIFICATION = 'DATA_VERIFICATION',
    PREVIEW_MODE = 'PREVIEW_MODE',
    SPECIAL_OFFER_ACTIVATION = 'SPECIAL_OFFER_ACTIVATION',
    CREATED_APP = 'CREATED_APP',
    UPDATED_APP = 'UPDATED_APP',
    INVALID_SSO_APP = 'INVALID_SSO_APP',
    NO_CHANGE_AFTER_DATA_SYNC = 'NO_CHANGE_AFTER_DATA_SYNC',
    FAILED_TRANSACTION_MESSAGE = 'FAILED_TRANSACTION_MESSAGE',
    FAILED_LOGIN = 'FAILED_LOGIN',
    FAILED_SSO = 'FAILED_SSO'
}

export const ProblemTypesMap = new Map<ProblemType, string>([
    [ProblemType.APPLICATION_WITHOUT_SCHEDULE, 'Not scheduled'],
    [ProblemType.EXPIRED_APPLICATION, 'Expired'],
    [ProblemType.EXPIRE_SOON_INTEGRATION, 'Expire soon'],
    [ProblemType.PENDING_APPLICATION, 'Pending'],
    [ProblemType.SUSPENDED_APPLICATION, 'Suspended'],
    [ProblemType.REJECTED_PP_APPLICATION, 'Rejected privacy policy'],
    [ProblemType.SUSPENDED_DUE_SCHOOL_YEAR_ROLL_OVER_PERIOD, 'School year rollover period'],
    [ProblemType.CONNECTION, 'Broken connection'],
    [ProblemType.WARN, 'Sync warnings'],
    [ProblemType.DATA_ACCESS, 'Data access'],
    [ProblemType.SCHOOL_MAPPING_WITH_VANISHED_SCHOOL, 'Schools with missing data'],
    [ProblemType.SCHOOL_MAPPING, 'Not mapped schools'],
    [ProblemType.FAILED_SYNC, 'Failed sync'],
    [ProblemType.SYNCHRONIZATION, 'Sync errors'],
    [ProblemType.CHANGES_PENDING_APPROVAL_BY_SYSADMIN, 'Changes pending your approval'],
    [ProblemType.CHANGES_PENDING_APPROVAL, 'Changes pending approval'],
    [ProblemType.DATA_VERIFICATION, 'Data verification'],
    [ProblemType.PREVIEW_MODE, 'Preview mode'],
    [ProblemType.SPECIAL_OFFER_ACTIVATION, 'Special offer activation'],
    [ProblemType.CREATED_APP, 'Created application'],
    [ProblemType.UPDATED_APP, 'Updated application'],
    [ProblemType.INVALID_SSO_APP, 'Invalid SSO application'],
    [ProblemType.NO_CHANGE_AFTER_DATA_SYNC, 'No data changes'],
    [ProblemType.FAILED_TRANSACTION_MESSAGE, 'Failed SSO Portal sync'],
    [ProblemType.FAILED_LOGIN, 'Failed login'],
    [ProblemType.FAILED_SSO, 'Failed SSO']
]);

export const ETX_ISSUES = [ProblemType.FAILED_LOGIN, ProblemType.FAILED_SSO];
export const ETX_REQUIRED_ISSUES = [
    ProblemType.FAILED_LOGIN,
    ProblemType.FAILED_SSO,
    ProblemType.INVALID_SSO_APP,
    ProblemType.FAILED_TRANSACTION_MESSAGE
];

export const LOGIN_INCIDENT_TYPE = 'LOGIN';
export const SSO_INCIDENT_TYPE = 'SSO';
export const INCIDENT_FILTER_BY_DISTRICT = 'district';
export const LOGIN_INCIDENT_FILTER_BY_IDP = 'idp';
export const SSO_INCIDENT_FILTER_BY_APPLICATION = 'application';
export const ETXProblemTypesMap = new Map<string, ProblemType>([
    [LOGIN_INCIDENT_TYPE, ProblemType.FAILED_LOGIN],
    [SSO_INCIDENT_TYPE, ProblemType.FAILED_SSO],
]);

export class ProblemTypesItem {
    constructor(public problem: ProblemType,
                public title: string) {
        this.problem = problem;
        this.title = title;
    }
}

export enum DataAccessExecutionStatus {
    START = 'START',
    END = 'END'
}

export enum DataAccessStepName {
    ACTIVE_INTEGRATION_STATE_ACCESS = 'ACTIVE_INTEGRATION_STATE_ACCESS',
    INACTIVE_INTEGRATION_STATE_ACCESS = 'INACTIVE_INTEGRATION_STATE_ACCESS',
    DISTRICT_SYNC_ACCESS = 'DISTRICT_SYNC_ACCESS',
    ORGANIZATION_DELETE_ACCESS = 'ORGANIZATION_DELETE_ACCESS',
    VALIDATION_OR_FILTRATION_ACCESS = 'VALIDATION_OR_FILTRATION_ACCESS'
}

export const DataAccessStepNameMap = new Map<DataAccessStepName, { friendlyName: string; tooltip: string; jobDetail: string }>([
    [DataAccessStepName.ACTIVE_INTEGRATION_STATE_ACCESS, {
        friendlyName: 'Approving request',
        tooltip: 'Request changes are being applied.',
        jobDetail: 'Request approved'
    }],
    [DataAccessStepName.INACTIVE_INTEGRATION_STATE_ACCESS, {
        friendlyName: 'Inactivating integration',
        tooltip: 'Inactivating or removing integration.',
        jobDetail: 'Integration inactivated'
    }],
    [DataAccessStepName.DISTRICT_SYNC_ACCESS, {
        friendlyName: 'Updating data',
        tooltip: 'Provider has added, updated or deleted entities during sync.',
        jobDetail: 'Data updated'
    }],
    [DataAccessStepName.ORGANIZATION_DELETE_ACCESS, {
        friendlyName: 'Removing organization',
        tooltip: 'Provider has removed organization.',
        jobDetail: 'Organization removed'
    }],
    [DataAccessStepName.VALIDATION_OR_FILTRATION_ACCESS, {
        friendlyName: 'Data validation or filtration',
        tooltip: 'Provider has changed filters or data requirements rules.',
        jobDetail: 'Validation or filtration changed'
    }]
]);


export interface IssueReportsContext {
    property: string;
    guidsSet: Set<string>;
}

export interface IPredefinedFilter {
    label: string;
    name: string;
    guid?: string;
    issues?: IssueReportItem[];
    hasCriticalIssue?: boolean;
    application?: Application;
    organization?: DirectoryOrganization;
}

export class IntegrationReportItem {
    defaultApplication: Application;
    organization: DirectoryOrganization;
    stateTitle: string;

    constructor(public organizationGuid: string,
                public applicationGuid: string,
                public defaultApplicationGuid: string,
                public integrationId: string,
                public mode: string,
                public state: string,
                public defaultSchemaName: string,
                public creationTs: Date = null,
                public notEduToneDname: string) {
        this.organizationGuid = organizationGuid;
        this.applicationGuid = applicationGuid;
        this.defaultApplicationGuid = defaultApplicationGuid;
        this.integrationId = integrationId;
        this.mode = mode;
        this.state = state;
        this.defaultSchemaName = defaultSchemaName;
        this.creationTs = creationTs ? new Date(creationTs) : null;
        this.notEduToneDname = notEduToneDname;
    }
}

export class ApplicationIntegrationsReportItem extends ApplicationGalleryModel {
    _integrations: IntegrationReportItem[] = [];
    filteredIntegrations: IntegrationReportItem[] = [];
    numberOfUniqueOrganizations = 0;
    percentOfTotalNumberFilteredIntegrations = 0;
    isLoading = false;
    isSuccessDownload = false;
    isDownloadFailed = false;

    static getNumberOfUniqueOrganizations(integrations: IntegrationReportItem[], onlyDistricts = true): number {
        const uniqueOrgs = new Set<string>();
        integrations.forEach((integration: IntegrationReportItem) => {
            if (integration.organization
                && (!onlyDistricts || (integration.organization.orgType === OrgType.DISTRICT))
                && !uniqueOrgs.has(integration.organizationGuid)
            ) {
                uniqueOrgs.add(integration.organizationGuid);
            }
        });
        return uniqueOrgs.size;
    }
}

export class VendorIntegrationsReportItem extends ApplicationGalleryModel {
    org: DirectoryOrganization;
    apps: ApplicationIntegrationsReportItem[];
    numberOfIntegrations = 0;
    isLoading = false;
    isSuccessDownload = false;
    isDownloadFailed = false;

    static getNumberOfIntegrations(vendor: VendorIntegrationsReportItem): number {
        return vendor.apps?.reduce((result: number, app: ApplicationIntegrationsReportItem) =>
            (result + app._integrations?.length),
            0
        );
    }
}

export class OrgsByRosterDataSizeReport {
    createdAt: Date;
    districtGuid: string;
    orgName: string;
    total: number;
    totalByEntities: {
        // Calendar: number,...
        [key: string]: number;
    };
}

export interface IApplicationsDashboardFilter {
    appTextSearch: string;
    vendorTextSearch: string;
    orgTextSearch: string;
    mode: string;
    dataSchema: string;
    startDate: Date;
    endDate: Date;
}

export class OrganizationReportItem extends DirectoryOrganization {
    integrations: IntegrationReportItem[] = [];
}

export class GeoStatesIntegrationsReportItem {
    key = '';
    _orgs: OrganizationReportItem[] = [];
    filteredOrgs: OrganizationReportItem[] = [];
    percentOfTotalNumberFilteredOrgs = 0;

    constructor(
        public country: string,
        public state: string,
        public region: string,
        public stats: StateStat,
        geoData: GeoMetadata,
        public countryName: string = '',
        public stateName: string = ''
    ) {
        this.setCountryStateNames(geoData);
    }

    setCountryStateNames(geoData: GeoMetadata) {
        if (!this.countryName) {
            const countryIndex = geoData.countries.findIndex((country: GeoCountry) => country.iso === this.country);
            if (countryIndex > -1) {
                this.countryName = geoData.countries[countryIndex].name;
            }
        }

        if (!this.stateName && geoData.states[this.country]) {
            const stateIndex = geoData.states[this.country].findIndex((state: GeoState) => state.stateIso === this.state);
            if (stateIndex > -1) {
                this.stateName = geoData.states[this.country][stateIndex].name;
            }
        }

        this.key = `${this.countryName || this.country}, ${this.stateName || this.state}`;
    }
}

export interface IGeoDashboardFilter {
    geoTextSearch: string;
    appTextSearch: string;
    orgTextSearch: string;
    orgType: string;
    startDate: Date;
    endDate: Date;
}

export const IdentityProvidersMap  = new Map<string, string>([
    ['UsernamePasswordOrgHostAuthentication', 'SchoolDay'],
    ['UsernamePasswordHostAuthentication', 'SchoolDay']
]);

export const ErrorTypeTitleMap  = new Map<string, string>([
    ['BadCredentialsException', 'Bad credentials'],
    ['VerificationRequestFailedException', 'Verification request failed'],
    ['CreateUserException', 'Create user'],
    ['IllegalStateException', 'Illegal state'],
    ['IllegalArgumentException', 'Illegal argument'],
    ['InvalidScopeException', 'Invalid scope'],
    ['EncryptionException', 'Encryption'],
    ['PasswordWeaknessException', 'Password weakness'],
    ['RemoteApiException', 'Remote API'],
    ['TokenServiceException', 'Token service'],
    ['NoSuchAlgorithmException', 'No such algorithm'],
    ['NonceExpiredException', 'Nonce expired'],
    ['UserNotFoundException', 'User not found'],
    ['InvalidClientIdException', 'Invalid clientId'],
    ['InvalidRequestException', 'Invalid request'],
    ['InvalidGrantException', 'Invalid grant'],
    ['UsernameNotFoundException', 'Username not found'],
    ['UnauthorizedClientException', 'Unauthorized client'],
    // SSO
    ['ACCESS_DENIED', 'Access denied'],
    ['VALIDATION', 'Validation'],
    ['SSO', 'SSO'],
    ['PROVISION', 'Provision'],
    ['SYSTEM', 'System'],
    ['UNKNOWN', 'Unknown']
]);

export class DraftIntegrationReportItem {
    appGuid: string;
    integrationId: string;
    notEduToneDname: string;
    vendorOrgGuid: string;
}


